import { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { cn } from '@/lib/utils';
import { triggerHaptic } from '@/hooks/useHaptics';
interface SaveButtonProps {
  saved: boolean;
  onToggle: () => void;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'overlay';
  className?: string;
  disabled?: boolean;
}

interface Particle {
  id: number;
  angle: number;
  distance: number;
  size: number;
  color: string;
}

export function SaveButton({ 
  saved, 
  onToggle, 
  size = 'md',
  variant = 'default',
  className,
  disabled = false,
}: SaveButtonProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isUnsaving, setIsUnsaving] = useState(false);
  const [particles, setParticles] = useState<Particle[]>([]);
  const [showParticles, setShowParticles] = useState(false);

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };


  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (disabled) return;
    
    const wasSaved = saved;
    onToggle();
    
    // Haptic feedback - success pattern for save, light for unsave
    if (!wasSaved) {
      triggerHaptic('success');
      setIsAnimating(true);
      setShowParticles(true);
      
      // Generate particles using primary brand color with varying opacity
      const opacities = [1, 0.9, 0.8, 0.7, 0.6];
      const newParticles: Particle[] = Array.from({ length: 8 }, (_, i) => ({
        id: i,
        angle: (i * 45) + (Math.random() * 20 - 10),
        distance: 20 + Math.random() * 15,
        size: 4 + Math.random() * 4,
        color: `hsl(var(--primary) / ${opacities[Math.floor(Math.random() * opacities.length)]})`,
      }));
      setParticles(newParticles);
      
      setTimeout(() => {
        setIsAnimating(false);
      }, 400);
      
      setTimeout(() => {
        setShowParticles(false);
        setParticles([]);
      }, 600);
    } else {
      triggerHaptic('light');
      setIsUnsaving(true);
      setTimeout(() => {
        setIsUnsaving(false);
      }, 350);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={disabled}
      className={cn(
        "relative flex items-center justify-center rounded-full transition-all duration-200",
        sizeClasses[size],
        variant === 'overlay' 
          ? "bg-black/40 backdrop-blur-md border border-white/20 shadow-md"
            + (saved ? "" : " text-white active:bg-black/60")
          : saved 
            ? "hover:bg-primary/10" 
            : "text-foreground hover:bg-black/5",
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}
      aria-label={saved ? "Remove from saved" : "Save"}
    >
      {/* Particles */}
      {showParticles && (
        <div className="absolute inset-0 pointer-events-none">
          {particles.map((particle) => (
            <span
              key={particle.id}
              className="absolute left-1/2 top-1/2 rounded-full animate-particle"
              style={{
                width: particle.size,
                height: particle.size,
                backgroundColor: particle.color,
                '--particle-angle': `${particle.angle}deg`,
                '--particle-distance': `${particle.distance}px`,
              } as React.CSSProperties}
            />
          ))}
        </div>
      )}
      
      {/* Icon with animation */}
      <Heart 
        className={cn(
          iconSizes[size],
          "transition-all duration-200",
          saved && "fill-primary text-primary",
          isAnimating && "animate-save-pop",
          isUnsaving && "animate-unsave-pop"
        )} 
        strokeWidth={2}
      />
    </button>
  );
}
